import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { TeamMember } from '@/models/team-member.model';

@Injectable({
  providedIn: 'root',
})
export class TeamMemberService {
  private http = inject(HttpClient);
  private oidcSecurityService = inject(OidcSecurityService);
  private apiUrl = `${window.location.origin}/api/team-memberships`;

  /**
   * JWT headers per the repo auth pattern
   * (`OidcSecurityService.getIdToken().pipe(switchMap(...))`) — the
   * /team-memberships API is guarded by JwtAuthGuard.
   */
  private authHeaders(): Observable<{ Authorization: string }> {
    return this.oidcSecurityService
      .getIdToken()
      .pipe(map((idToken) => ({ Authorization: `Bearer ${idToken}` })));
  }

  getTeamMembers(teamId: number): Observable<TeamMember[]> {
    // Get users with 'member' or 'supervisor' roles
    return this.authHeaders().pipe(
      switchMap((headers) =>
        this.http.get<TeamMember[]>(`${this.apiUrl}/by-team/${teamId}/members-supervisors`, { headers })
      )
    );
  }

  getAllTeamMembers(teamId: number): Observable<TeamMember[]> {
    // Get all members regardless of role (original endpoint)
    return this.authHeaders().pipe(
      switchMap((headers) => this.http.get<TeamMember[]>(`${this.apiUrl}/by-team/${teamId}`, { headers }))
    );
  }

  getTeamMembersAndSupervisors(teamId: number): Observable<TeamMember[]> {
    // Get members with 'member' or 'supervisor' roles only
    return this.authHeaders().pipe(
      switchMap((headers) =>
        this.http.get<TeamMember[]>(`${this.apiUrl}/by-team/${teamId}/members-supervisors`, { headers })
      )
    );
  }

  addMember(teamId: number, userId: string, roles: string[]): Observable<TeamMember> {
    return this.authHeaders().pipe(
      switchMap((headers) =>
        this.http.post<TeamMember>(
          `${this.apiUrl}`,
          {
            userId: Number(userId),
            teamId: teamId,
            roles: roles,
          },
          { headers }
        )
      )
    );
  }

  removeMember(teamId: number, userId: number, membershipId: number): Observable<any> {
    return this.authHeaders().pipe(
      switchMap((headers) => this.http.delete(`${this.apiUrl}/${membershipId}`, { headers }))
    );
  }

  updateMemberRoles(teamId: number, userId: string, roles: string[]): Observable<TeamMember> {
    // First need to find the membership ID by teamId and userId
    return this.authHeaders().pipe(
      switchMap((headers) =>
        this.http.get<any[]>(`${this.apiUrl}/by-user/${userId}`, { headers }).pipe(
          switchMap((memberships) => {
            const membership = memberships.find((m) => m.teamId === teamId);
            if (membership) {
              return this.http.patch<TeamMember>(`${this.apiUrl}/${membership.id}`, { roles }, { headers });
            }
            return throwError(() => new Error('Membership not found'));
          })
        )
      )
    );
  }

  // Final Reviewer methods
  getFinalReviewers(teamId: number): Observable<TeamMember[]> {
    return this.authHeaders().pipe(
      switchMap((headers) => this.http.get<TeamMember[]>(`${this.apiUrl}/final-reviewers/${teamId}`, { headers }))
    );
  }

  addFinalReviewer(teamId: number, userId: string): Observable<TeamMember> {
    return this.authHeaders().pipe(
      switchMap((headers) =>
        this.http.post<TeamMember>(
          `${this.apiUrl}/final-reviewers`,
          {
            teamId: teamId,
            userId: Number(userId),
          },
          { headers }
        )
      )
    );
  }

  removeFinalReviewer(teamId: number, userId: string): Observable<any> {
    return this.authHeaders().pipe(
      switchMap((headers) => this.http.delete(`${this.apiUrl}/final-reviewers/${teamId}/${userId}`, { headers }))
    );
  }

  // Finance Reviewer methods
  getFinanceReviewers(teamId: number): Observable<TeamMember[]> {
    return this.authHeaders().pipe(
      switchMap((headers) => this.http.get<TeamMember[]>(`${this.apiUrl}/finance-reviewers/${teamId}`, { headers }))
    );
  }

  addFinanceReviewer(teamId: number, userId: string): Observable<TeamMember> {
    return this.authHeaders().pipe(
      switchMap((headers) =>
        this.http.post<TeamMember>(
          `${this.apiUrl}/finance-reviewers`,
          {
            teamId: teamId,
            userId: Number(userId),
          },
          { headers }
        )
      )
    );
  }

  removeFinanceReviewer(teamId: number, userId: string): Observable<any> {
    return this.authHeaders().pipe(
      switchMap((headers) => this.http.delete(`${this.apiUrl}/finance-reviewers/${teamId}/${userId}`, { headers }))
    );
  }

  // Gemba Lead methods
  getGembaLeads(teamId: number): Observable<TeamMember[]> {
    return this.authHeaders().pipe(
      switchMap((headers) => this.http.get<TeamMember[]>(`${this.apiUrl}/gemba-leads/${teamId}`, { headers }))
    );
  }

  addGembaLead(teamId: number, userId: string): Observable<TeamMember> {
    return this.authHeaders().pipe(
      switchMap((headers) =>
        this.http.post<TeamMember>(
          `${this.apiUrl}/gemba-leads`,
          {
            teamId: teamId,
            userId: Number(userId),
          },
          { headers }
        )
      )
    );
  }

  removeGembaLead(teamId: number, userId: string): Observable<any> {
    return this.authHeaders().pipe(
      switchMap((headers) => this.http.delete(`${this.apiUrl}/gemba-leads/${teamId}/${userId}`, { headers }))
    );
  }

  // Workspace Lead methods (read-only; mutations ride the combined
  // PATCH /teams/:id/workspace-management-settings)
  getWorkspaceLeads(teamId: number): Observable<TeamMember[]> {
    return this.authHeaders().pipe(
      switchMap((headers) => this.http.get<TeamMember[]>(`${this.apiUrl}/workspace-leads/${teamId}`, { headers }))
    );
  }
}
